import React, { useEffect, useState } from "react";

// Shown after a file has been completed. Completion is already saved by the
// time this appears, so dismissing it, navigating away, or a failed request
// can never leave a file half-completed.
//
// The next year's task can be adjusted before it is created: its name, year
// end, due date and who it is assigned to. The fields open on exactly what the
// server would create (`proposal`, from GET /api/tasks/{id}/roll-forward/), and
// only the ones the person actually changes are sent back as overrides -- an
// untouched field stays the engine's call.
//
// Presentational on purpose: it makes no API calls, so RollForwardFlow owns the
// requests and this can be tested without mocking anything.
export default function RollForwardPrompt({
  clientName, year, nextYear, onConfirm, onDismiss, busy, unapproved,
  onOverride,
  proposal, employees = [], error,
}) {
  const [name, setName] = useState("");
  const [yearEnd, setYearEnd] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [assigned, setAssigned] = useState([]);
  const [note, setNote] = useState("");
  const [archive, setArchive] = useState(true);

  // Re-seed the form when the proposal arrives. Keyed on the proposal object,
  // so a re-render with the same proposal never wipes what was typed.
  useEffect(() => {
    if (!proposal || !proposal.eligible) return;
    setName(proposal.name || "");
    setYearEnd(proposal.year_end_date || "");
    setDueDate(proposal.due_date || "");
    setAssigned((proposal.employee_ids || []).map(Number));
  }, [proposal]);

  const blocked = !!unapproved;
  const loading = !blocked && proposal == null;
  const ineligible = !blocked && proposal && !proposal.eligible;

  // The heading follows an edited year end, the same way the server derives
  // the new task's fiscal year from it.
  const editedYear = yearEnd ? Number(yearEnd.slice(0, 4)) : null;
  const shownNextYear = editedYear || proposal?.fiscal_year || nextYear;

  const confirm = () => {
    const overrides = {};
    if (proposal) {
      if (name !== (proposal.name || "")) overrides.name = name;
      if (yearEnd !== (proposal.year_end_date || "")) {
        overrides.year_end_date = yearEnd || null;
      }
      if (dueDate !== (proposal.due_date || "")) {
        overrides.due_date = dueDate || null;
      }
      const before = [...(proposal.employee_ids || [])].map(Number).sort();
      const after = [...assigned].sort();
      if (before.join(",") !== after.join(",")) overrides.employee_ids = after;
    }
    onConfirm({ overrides, archiveSource: archive, note: note.trim() });
  };

  const toggleAssigned = (id) =>
    setAssigned((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,.35)",
      display: "grid", placeItems: "center", zIndex: 4000,
    }}>
      <div role="dialog" aria-modal="true" style={{
        background: "#fff", borderRadius: 10, padding: 20, width: 460,
        maxWidth: "92vw", maxHeight: "88vh", overflowY: "auto",
        boxShadow: "0 20px 60px rgba(0,0,0,.3)",
      }}>
        {blocked ? (
          <>
            <h3 style={{ margin: "0 0 10px", fontSize: 17 }}>
              Cannot roll forward yet
            </h3>
            <p style={{ margin: "0 0 16px", color: "#374151", fontSize: 14 }}>
              {unapproved.entries} time {unapproved.entries === 1 ? "entry" : "entries"}
              {" "}({unapproved.hours} hrs) on {clientName} {unapproved.year_end}
              {" "}are still awaiting approval. Approve them first, then use
              "Roll Forward to Next Year" -- the file is still completed.
            </p>
            {unapproved.can_override && onOverride ? (
              <p style={{ margin: "0 0 16px", color: "#6b7280", fontSize: 13 }}>
                As a manager you can roll forward anyway. The {unapproved.year_end}
                {" "}year closes for new time, but the waiting entries stay where
                they are and can still be approved.
              </p>
            ) : null}
            {error ? (
              <p style={{ margin: "0 0 12px", color: "#b91c1c", fontSize: 13 }}>{error}</p>
            ) : null}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              {unapproved.can_override && onOverride ? (
                <button onClick={onOverride} disabled={busy} style={btn(false)}>
                  {busy ? "Rolling forward…" : "Roll forward anyway"}
                </button>
              ) : null}
              <button onClick={onDismiss} style={btn(true)}>OK</button>
            </div>
          </>
        ) : ineligible ? (
          <>
            <h3 style={{ margin: "0 0 10px", fontSize: 17 }}>
              Nothing to roll forward
            </h3>
            <p style={{ margin: "0 0 16px", color: "#374151", fontSize: 14 }}>
              {clientName}{year ? ` ${year}` : ""} cannot be rolled forward
              {proposal.reason ? ` — ${proposal.reason}` : ""}. The file is
              still completed.
            </p>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button onClick={onDismiss} style={btn(true)}>OK</button>
            </div>
          </>
        ) : (
          <>
            <h3 style={{ margin: "0 0 10px", fontSize: 17 }}>
              Roll {clientName} forward to {shownNextYear}?
            </h3>
            <p style={{ margin: "0 0 14px", color: "#374151", fontSize: 14 }}>
              Completing the {year} file can carry it forward to {shownNextYear}.
              Late time can still go on the {year} file for 3 months;
              then {year} locks for {clientName}. Lock it sooner from the task page.
            </p>

            {loading ? (
              <p style={{ color: "#6b7280", fontSize: 13 }}>
                Loading next year's task…
              </p>
            ) : (
              <div style={{ display: "grid", gap: 10, marginBottom: 14 }}>
                <label style={lbl}>
                  Task name
                  <input type="text" value={name} placeholder="(client name and year)"
                         onChange={(e) => setName(e.target.value)} style={input} />
                </label>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  <label style={lbl}>
                    Year end
                    <input type="date" value={yearEnd}
                           onChange={(e) => setYearEnd(e.target.value)} style={input} />
                  </label>
                  <label style={lbl}>
                    Due date
                    <input type="date" value={dueDate}
                           onChange={(e) => setDueDate(e.target.value)} style={input} />
                  </label>
                </div>
                {employees.length > 0 && (
                  <fieldset style={{ border: "1px solid #e5e7eb", borderRadius: 7,
                                     padding: "6px 10px", margin: 0 }}>
                    <legend style={{ ...lbl, padding: "0 4px" }}>Assigned to</legend>
                    <div style={{ maxHeight: 120, overflowY: "auto",
                                  display: "grid", gap: 2 }}>
                      {employees.map((e) => (
                        <label key={e.id} style={check}>
                          <input type="checkbox"
                                 checked={assigned.includes(Number(e.id))}
                                 onChange={() => toggleAssigned(Number(e.id))} />
                          {e.name || e.full_name}
                        </label>
                      ))}
                    </div>
                  </fieldset>
                )}
                <label style={lbl}>
                  Note (optional)
                  <textarea value={note} rows={3}
                            placeholder={`Added to ${clientName}'s notes`}
                            onChange={(e) => setNote(e.target.value)}
                            style={{ ...input, resize: "vertical" }} />
                </label>
                <label style={check}>
                  <input type="checkbox" checked={archive}
                         onChange={(e) => setArchive(e.target.checked)} />
                  Archive the completed {year} task
                </label>
              </div>
            )}

            {error && (
              <p role="alert" style={{ color: "#b91c1c", fontSize: 13, margin: "0 0 12px" }}>
                {error}
              </p>
            )}

            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button onClick={onDismiss} disabled={busy} style={btn(false)}>
                Not now
              </button>
              <button onClick={confirm} disabled={busy || loading} style={btn(true)}>
                {busy ? "Working…" : "Roll forward and close"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const lbl = { display: "grid", gap: 4, fontSize: 12, fontWeight: 600, color: "#374151" };
const input = {
  padding: "6px 8px", border: "1px solid #d1d5db", borderRadius: 6,
  fontSize: 13, fontWeight: 400, fontFamily: "inherit",
};
const check = { display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#374151" };

const btn = (primary) => ({
  background: primary ? "#5b5bd6" : "#f3f4f6",
  color: primary ? "#fff" : "#374151",
  border: "none", borderRadius: 7, padding: "8px 14px",
  cursor: "pointer", fontWeight: 600, fontSize: 13,
});
